import { inject } from "@angular/core";
import {
  IAppointment,
  IAppointmentRouting,
  IScheduledAppointmentContact,
  ITicket,
} from "../../../core-ui/models";
import {
  CanActivateFn,
  Router,
  UrlTree,
} from "@angular/router";
import {
  ActivityIndicatorService,
  AppointmentService,
  extractErrorMessage,
  PublicTicketService,
  ThemeService,
  UserService,
} from "../../../core-ui";
import {
  ErrorPageCode,
  ErrorPageSubCode,
  PARAM_APPOINTMENT_GUEST,
  PARAM_CONVERSATION_CHANNEL,
  PARAM_CONVERSATION_ORIGIN,
} from "../../app.enums";
import {
  ConversationOriginEnum,
  PARAM_TICKET_ID,
  UserRoleEnum,
} from "../../../core-ui/core-ui.enums";
import {
  IWidgetParams as IConfigOptions,
  IContactInfo,
} from "@auvious/integrations";
import { ConversationService } from "../../../app/services/conversation.service";

class HandledError extends Error {
  subcode: string;
  code: any;

  constructor(
    code: ErrorPageCode,
    message?: string,
    subcode?: ErrorPageSubCode
  ) {
    super(message || code);
    this.code = code;
    this.subcode = subcode;
  }
}

export const appointmentConversationGuard: CanActivateFn = async (
  route,
  state,
): Promise<boolean | UrlTree> => {
  const themeService = inject(ThemeService);
  const ticketService = inject(PublicTicketService);
  const appointmentService = inject(AppointmentService);
  const conversation = inject(ConversationService);
  const router = inject(Router);
  const activity = inject(ActivityIndicatorService);
  const user = inject(UserService);

  const handleAppointment = (appointment: IAppointment<IAppointmentRouting>) => {
    if (!appointment) {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_NOT_FOUND,
        "Appointment could not be found"
      );
    }

    if (appointment.interaction.state === "COMPLETE") {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_COMPLETED,
        "Appointment is completed"
      );
    }

    if (appointment.interaction.state === "EXPIRED") {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_EXPIRED,
        "Appointment has expired"
      );
    }

    appointmentService.notifyChange(appointment);
  };

  const onError = (
    error,
    appointmentId?: string,
    userMessage?: string,
    chatMode?: string,
    routingType?: string,
    participantId?: string
  ): UrlTree => {
    activity.loading(false);

    let errorPageCode, code, message;

    if (error instanceof HandledError) {
      code = error.code;
      errorPageCode = error.code;
      message = error.message;
    } else {
      code =
        error.responseJSON.status ||
        error.response.status ||
        ErrorPageCode.GENERIC_ERROR;
      errorPageCode = ErrorPageCode.GENERIC_ERROR;
      message = extractErrorMessage(error);
    }
    // log error to appointment as well
    if (appointmentId && code !== ErrorPageCode.APPOINTMENT_COMPLETED) {
      let systemMessage = `Error code: ${code}, message: ${message}`;
      if (chatMode) systemMessage += `, integration: ${chatMode}`;
      if (routingType) systemMessage += `, routing type: ${routingType}`;

      // update state
      appointmentService.updateState(appointmentId, "failed", {
        systemMessage,
        userMessage,
      });

      // TODO: commented out for now as the backend is not yet ready
      // comment in when issue #766 is ready
      /*
      if (participantId) {
        appointmentService
          .participantFailed(appointmentId, participantId, {
            systemMessage,
            userMessage: userMessage || "Could not start a new conversation",
          })
          .catch(() => {});
      }
      */
    }
    return router.createUrlTree(["/error", errorPageCode], {
      queryParams: { message: userMessage },
    });
  };

  const getTicket = async (ticket: string): Promise<ITicket> => {
    try {
      if (!ticket) {
        throw new HandledError(
          ErrorPageCode.TICKET_NOT_FOUND,
          "Ticket not found in URL"
        );
      }

      const ticketData = await ticketService.getTicket(ticket);

      if (ticketData.properties.theme) {
        themeService.setOptions(ticketData.properties.theme);
      }

      return ticketData;
    } catch (ex) {
      throw new HandledError(
        ErrorPageCode.TICKET_NOT_FOUND,
        "Could not retrieve ticket details"
      );
    }
  };

  const getAppointment = async (
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> => {
    try {
      const appointment = await appointmentService.get(appointmentId);
      handleAppointment(appointment);
      return appointment;
    } catch (ex) {
      if (ex instanceof HandledError) throw ex;
      else
        throw new HandledError(
          ErrorPageCode.APPOINTMENT_NOT_FOUND,
          "Could not retrieve appointment details"
        );
    }
  };

  const startAppointment = async (
    appointmentId: string,
    participantId: string
  ): Promise<IAppointment<IAppointmentRouting>> => {
    try {
      const appointment = await appointmentService.updateState(
        appointmentId,
        "starting",
        { participantId }
      );
      handleAppointment(appointment);
      return appointment;
    } catch (ex) {
      if (ex instanceof HandledError) throw ex;
      if (ex?.response?.status === 409) throw ex;
      if (ex?.response?.status === 404)
        throw new HandledError(
          ErrorPageCode.APPOINTMENT_NOT_FOUND,
          "404: Could not update appointment state"
        );
      throw ex;
    }
  };

  const getConversationRequest = (
    ticket: ITicket,
    participant: IScheduledAppointmentContact,
    appointment: IAppointment<IAppointmentRouting>
  ): IContactInfo => {
    try {
      const request = conversation.prepareConversationRequest(
        ticket,
        appointment,
        participant
      );
      return request;
    } catch (ex) {
      throw new HandledError(ErrorPageCode.CONVERSATION_FAILED);
    }
  };

  const goToRoomAsGuestCustomer = (
    ticket: ITicket,
    appointment: IAppointment<IAppointmentRouting>
  ) => {
    //   // mark user as guest customer
    const usr = user.getActiveUser();
    usr.addRole(UserRoleEnum.guestCustomer);
    user.setActiveUser(usr);

    // send to room
    const urlTree = state.url.split("/")?.filter((p) => !!p);
    const pagePath = urlTree.length > 0 ? urlTree[0] : "";

    return pagePath === "t"
      ? true
      : router.createUrlTree(["/t", ticket.id], {
          queryParams: {
            [PARAM_CONVERSATION_ORIGIN]: ConversationOriginEnum.APPOINTMENT,
            [PARAM_APPOINTMENT_GUEST]: 1,
            [PARAM_CONVERSATION_CHANNEL]:
              conversation.getConversationChatMode(appointment),
          },
        });
  };

  activity.loading(true);

  let ticket: ITicket,
    appointment: IAppointment<IAppointmentRouting>,
    participant: IScheduledAppointmentContact,
    config: IConfigOptions,
    request: IContactInfo,
    appointmentId: string,
    participantId: string;

  const ticketCode = route.paramMap.get(PARAM_TICKET_ID);

  // 1. get the ticket data
  try {
    ticket = await getTicket(ticketCode);
    if (!ticket.properties.appointmentId) {
      return true;
    }
    appointmentId = ticket.properties.appointmentId;
    participantId = ticket.properties.customer_id;
  } catch (ex) {
    return onError(ex);
  }

  // 1.5. Check if someone used my ticket. If it was me on refresh I would be able to reconnect
  try {
    appointment = await getAppointment(appointmentId);
    if (
      appointment?.interaction?.initiatedBy?.id ===
        ticket.properties.customer_id &&
      // since I started it, I should be able to reconnect (have data in session store)
      !conversation.isReconnect(ticketCode)
    ) {
      return goToRoomAsGuestCustomer(ticket, appointment);
    }
  } catch (e) {
    return onError(
      e,
      appointmentId,
      "Could not process appointment request",
      undefined,
      undefined,
      participantId
    );
  }

  // 2. update the status of the appointment to avoid race conditions
  try {
    appointment = await startAppointment(
      appointmentId,
      ticket.properties.customer_id
    );
  } catch (ex) {
    if (ex?.response?.status == 409) {
      // we have a conflict, call has already been initiated by another participant
      try {
        // appointment = await getAppointment(appointmentId);
        // I did not start the conversatione
        if (
          !!appointment.interaction.initiatedBy &&
          appointment.interaction.initiatedBy.id !==
            ticket.properties.customer_id
        ) {
          return goToRoomAsGuestCustomer(ticket, appointment);
        }
        return onError(
          new HandledError(
            ErrorPageCode.APPOINTMENT_ERROR,
            "Could not join the appointment"
          ),
          appointmentId,
          undefined,
          undefined,
          undefined,
          participantId
        );
      } catch (e) {
        return onError(
          e,
          appointmentId,
          "Could not process appointment request",
          undefined,
          undefined,
          participantId
        );
      }
    }
  }

  if (
    // conversation already initialized, same guard is used in another page, do not repeat flow below
    conversation.isConversationInitialised
  ) {
    return true;
  }

  // 2. get the participant details
  try {
    participant = await appointmentService.getParticipantDetails(
      ticket.properties.appointmentId,
      ticket.properties.customer_id
    );
  } catch (ex) {
    return onError(
      ex,
      appointmentId,
      "Could not retrieve participant details",
      undefined,
      undefined,
      participantId
    );
  }

  // 3. start a conversation (or reconnect to existing one)
  try {
    config = conversation.prepareConversationConfig(ticket, appointment);
    request = getConversationRequest(ticket, participant, appointment);

    await conversation.init(config, ticketCode);
    await conversation.connect(request, ticketCode);

    // was used by conferenceComponent.leaveApppointment' to end the conversation as customer
    // conversation.setConversationOriginCustomer(appointment.id);

    activity.loading(false);

    return true;
  } catch (ex) {
    return onError(
      ex,
      appointmentId,
      "Could not start a new conversation",
      config?.chatMode,
      appointment?.interaction?.routing?.type,
      participantId
    );
  }
};
